import { signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import {
  ref,
  onValue,
  onDisconnect,
  set,
  serverTimestamp,
  off,
} from 'firebase/database';
import { auth, rtdb } from 'src/utils/firebase';
import axios, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------
// RTDB-based user presence.
//
// Flow:
//   1. App user signs in (JWT in localStorage, user object in auth ctx).
//   2. startPresenceTracking(userId) is called from auth-provider when
//      state.user changes.
//   3. We GET /api/auth/firebase-token to mint a Firebase custom token
//      with uid === user.id.toString().
//   4. signInWithCustomToken makes that uid available to RTDB rules
//      (`auth.uid === $uid`) so we can write under /status/{userId}.
//   5. We watch `.info/connected`. Whenever the client transitions to
//      online, we queue an onDisconnect that flips state to "offline"
//      with the server's timestamp, then write the live "online" state.
//
// Why RTDB and not Firestore: only RTDB has onDisconnect, which the
// server fires from infrastructure when the client socket drops. That's
// the only reliable way to mark a user offline — heartbeats can lie if
// the tab is just frozen.
// ----------------------------------------------------------------------

const STATUS_PATH = (uid) => `status/${uid}`;

let activeUserId = null;
let unsubscribeAuthState = null;
let unsubscribeConnected = null;
let connectingPromise = null;

async function fetchFirebaseCustomToken() {
  const res = await axios.get(API_ENDPOINTS.auth.firebaseToken);
  const token = res.data?.token || res.data?.data?.token;
  if (!token) throw new Error('Firebase custom token not returned by /api/auth/firebase-token');
  return token;
}

function waitForFirebaseUid(expectedUid) {
  return new Promise((resolve) => {
    if (auth.currentUser && auth.currentUser.uid === expectedUid) {
      resolve(auth.currentUser);
      return;
    }
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && firebaseUser.uid === expectedUid) {
        unsub();
        resolve(firebaseUser);
      }
    });
  });
}

function attachConnectedListener(userId) {
  const userStatusRef = ref(rtdb, STATUS_PATH(userId));
  const connectedRef = ref(rtdb, '.info/connected');

  unsubscribeConnected = onValue(connectedRef, (snap) => {
    if (snap.val() === false) {
      // Client is currently offline. The server will trigger our previously
      // registered onDisconnect, so there's nothing to do client-side.
      return;
    }
    // Client just (re-)connected. Register the offline-on-disconnect
    // handler FIRST, so a crash between steps still leaves the user
    // marked offline. Only then write the online state.
    onDisconnect(userStatusRef)
      .set({ state: 'offline', lastSeen: serverTimestamp() })
      .then(() => {
        set(userStatusRef, { state: 'online', lastSeen: serverTimestamp() }).catch(() => {});
      })
      .catch((err) => {
        // Most common cause: rules failed because Firebase Auth uid
        // doesn't match $uid. Surface in console but don't throw.
        // eslint-disable-next-line no-console
        console.warn('[presence] onDisconnect setup failed:', err?.message || err);
      });
  });
}

export async function startPresenceTracking(userId) {
  if (!userId) return;
  const uid = String(userId);
  if (activeUserId === uid) return; // already running for this user
  if (connectingPromise) {
    // Another start is already in flight — wait for it to settle and bail
    // (or no-op if it ended up tracking the same user).
    try { await connectingPromise; } catch (_) {}
    if (activeUserId === uid) return;
  }
  await stopPresenceTracking();
  activeUserId = uid;

  connectingPromise = (async () => {
    try {
      const token = await fetchFirebaseCustomToken();
      await signInWithCustomToken(auth, token);
      await waitForFirebaseUid(uid);
      attachConnectedListener(uid);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[presence] failed to start tracking:', err?.message || err);
      activeUserId = null;
    } finally {
      connectingPromise = null;
    }
  })();
  return connectingPromise;
}

export async function stopPresenceTracking() {
  if (unsubscribeConnected) {
    unsubscribeConnected();
    unsubscribeConnected = null;
  }
  if (unsubscribeAuthState) {
    unsubscribeAuthState();
    unsubscribeAuthState = null;
  }
  if (activeUserId) {
    try {
      // Best-effort explicit offline write (covers logout where the socket
      // stays open and onDisconnect won't fire).
      await set(ref(rtdb, STATUS_PATH(activeUserId)), {
        state: 'offline',
        lastSeen: serverTimestamp(),
      });
    } catch (_) {
      /* non-fatal */
    }
  }
  activeUserId = null;
}

// ----------------------------------------------------------------------
// Subscribe to presence for a list of user ids. Same callback shape as
// the previous Firestore-backed implementation, so chat-view.js doesn't
// need to change:
//   callback({ [uid]: { online: boolean, lastSeen: number | null } })
// ----------------------------------------------------------------------
export function subscribeToPresence(userIds, callback) {
  const ids = (userIds || []).filter((v) => v != null).map(String);
  if (ids.length === 0) return () => {};

  const aggregated = Object.fromEntries(ids.map((uid) => [uid, { online: false, lastSeen: null }]));
  let cancelled = false;

  const refs = ids.map((uid) => {
    const r = ref(rtdb, STATUS_PATH(uid));
    const handler = onValue(r, (snap) => {
      if (cancelled) return;
      const v = snap.val();
      const lastSeen = v?.lastSeen != null ? Number(v.lastSeen) : null;
      aggregated[uid] = {
        online: v?.state === 'online',
        lastSeen,
      };
      callback({ ...aggregated });
    });
    return { r, handler };
  });

  return () => {
    cancelled = true;
    refs.forEach(({ r }) => off(r));
  };
}

// ----------------------------------------------------------------------
// Backwards-compatible aliases. Older callers (auth-provider) still
// import `startPresenceHeartbeat` / `stopPresenceHeartbeat` — keep them
// pointing at the new RTDB implementation so we don't break the world.
// ----------------------------------------------------------------------
export const startPresenceHeartbeat = startPresenceTracking;
export const stopPresenceHeartbeat = stopPresenceTracking;
